import { Route } from 'react-router-dom';
import {
  IonAlert,
  IonApp,
  IonModal,
  IonRouterOutlet,
  setupIonicReact,
  useIonRouter
} from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { debounce } from 'lodash';
import packageInfo from "../package.json";
import { Network } from '@capacitor/network';
import { App as CapacitorApp } from '@capacitor/app';
import { ScreenOrientation } from '@capacitor/screen-orientation';
import { SplashScreen } from '@capacitor/splash-screen';
import axios from 'axios';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

import './App.css';
import './theme/custom-tab-bar.css';

import { useTranslation } from 'react-i18next';
import { Suspense, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from './services/redux/hook';
import { UserInfo, WalletInfoState } from './types/types';
import { setWalletInfo } from './services/redux/slices/walletInfo/wallet';
import { Capacitor } from '@capacitor/core';
import { setSupportedAssets } from './services/redux/slices/supportedAssets/supportedAssets';
import { setSupportedPools } from './services/redux/slices/supportedPools/supportedPools';
import { setRecoverServers } from './services/redux/slices/recoverServers/recoverServers';
import AllSupportedAssets from './assets.json';
import AllRecoverservers from './recover-servers.json';
import blockedRegions from './blocked-regions.json';
import UnsupportedRegionModal from './components/unsupportedRegion-Modal/UnsupportedRegion';
import AllSupportedPools from './supported-pools.json';
import { setLanguage } from './services/redux/slices/language/language';
import { useDeviceStorage } from './services/device_storage/useDeviceStorage';

import WelcomePage from './pages/welcome/Welcome';
import TabsPage from './pages/tabs/TabsPage';

import NamePage from './pages/registration/name/NamePage';
import EmailPage from './pages/registration/email/EmailPage';

import EmailVerificationPage from './pages/registration/email-verification/EmailVerificationPage';


import PhonePage from './pages/registration/phone/PhonePage';

import PhoneVerificationPage from './pages/registration/phone-verification/PhoneVerificationPage';
import GenerateWalletIntroPage from './pages/registration/generate-wallet/GenerateWallet';
import SecureWalletInfoPage from './pages/registration/secure-wallet-info/SecureWalletInfo';

import LoginWalletPage from './pages/login/login-wallet';
import InitPage from './pages/init/InitPage';
import KycFlowIntroPage from './pages/kyc_flow/kyc_intro/KycFlowIntroPage';
import KycProcessing from './pages/kyc_flow/kyc_processing/KycProcessing';
import KycRejected from './pages/kyc_flow/kyc_rejected/KycRejected';

import PinCreatePage from './pages/wallet-pin/pin-create/PinCreatePage';
import PinVerifyPage from './pages/wallet-pin/pin-verify/PinVerifyPage';
import PinLockPage from './pages/wallet-pin/pin-lock/PinLockPage';
import LoginWithEmail from './pages/login/email/LoginWithEmailPage';

import * as Sentry from '@sentry/react';
import AppUrlListener from './components/AppUrlListener/AppUrlListener';

import LockScreenModal from './components/LockScreenModal/LockScreenModal';
import { setInitialTheme } from './services/redux/slices/theme/theme';
import { showLockScreen, hideLockScreen, setInactivityTimeout, clearInactivityTimeout } from './services/redux/slices/lockScreen/lockScreen';

import { AppProvider } from './services/context/AppContext';
import { Browser } from '@capacitor/browser';
import { SafeArea } from 'capacitor-plugin-safe-area';
import LoginPhone from './pages/login/phone/LoginPhone';
import LoginPhoneVerifyPage from './pages/login/phone/LoginPhoneVerify';
import LoginEmailVerify from './pages/login/email/LoginEmailVerify';

const sentryDNS = import.meta.env.VITE_SENTRY_DNS;
const appStoreUrlIOS = import.meta.env.VITE_APP_STORE_URL;
const appStoreUrlAndroid = import.meta.env.VITE_PLAY_STORE_URL;
const apiBaseUrl = import.meta.env.VITE_EBIORO_API;

Sentry.init({
  dsn: `${sentryDNS}`,
  release: `${packageInfo.name}@${packageInfo.version}`,
  dist: "1",
  ignoreErrors: ['ResizeObserver loop limit exceeded'],
  tracesSampleRate: 1.0,
});

const isAndroid = Capacitor.getPlatform() === 'android';

setupIonicReact({
  mode: 'ios',
  hardwareBackButton: isAndroid,
  animated: true,
  swipeBackEnabled: !isAndroid
});

function RootComponent() {


  const appRef = useRef<any>(null);


  const environment = import.meta.env.VITE_ENVIRONMENT;

  const storage = useDeviceStorage();
  const { t, i18n } = useTranslation();
  const [isKey, setIsKey] = useState(false);
  const dispatch = useAppDispatch();
  const userInfo: UserInfo = useAppSelector((state) => state.user);
  const language = useAppSelector((state: any) => state.language);
  const hasResumed = useRef(false);


  const darkMode = useAppSelector((state) => state.theme.darkMode);
  const ionRouter = useIonRouter();
  const [showAlert, setShowAlert] = useState(false);
  const [versionAlert, setShowVersionAlert] = useState(false); // optional update
  const [forceUpdateAlert, setForceUpdateAlert] = useState(false); // blocking update
  const [showSafeAreaAlert, setShowSafeAreaAlert] = useState(false);
  const [safeAreaValues, setSafeAreaValues] = useState<{top: string, right: string, bottom: string, left: string}>({
    top: '', right: '', bottom: '', left: ''
  });
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const [appStoreUrl, setAppStoreUrl] = useState<string | null>(null);
  const isLockScreenVisible = useAppSelector((state) => state.lockScreen.isLockScreenVisible);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isBlockedRegion, setIsBlockedRegion] = useState(false);

  const showNetworkAlert = async () => {
    setShowAlert(true);

  }


  const resetInactivityTimer = debounce(() => {

    dispatch(clearInactivityTimeout());

    const newTimeoutId = setTimeout(() => {


      dispatch(showLockScreen());


    }, 3 * 60 * 1000); // 3 minutes of inactivity

    dispatch(setInactivityTimeout(newTimeoutId));
  }, 500); // 500ms debounce to prevent rapid resets

  function getcustomConfig() {


    if (environment === "development") {

      dispatch(setSupportedAssets(AllSupportedAssets.development))
      dispatch(setSupportedPools(AllSupportedPools.development))
      dispatch(setRecoverServers(AllRecoverservers.development))

    }
    else if (environment === "production") {

      dispatch(setSupportedAssets(AllSupportedAssets.production));
      dispatch(setSupportedPools(AllSupportedPools.production));
      dispatch(setRecoverServers(AllRecoverservers.production));

    } else {
      dispatch(setSupportedAssets(AllSupportedAssets.staging));
      dispatch(setSupportedPools(AllSupportedPools.staging));
      dispatch(setRecoverServers(AllRecoverservers.staging));

    }

  }

  const setDeviceLanguage = async () => {

    if (!userInfo.locale) return;

    const languageCode = userInfo.locale;
    await i18n.changeLanguage(languageCode);
    dispatch(setLanguage(languageCode))


  }

  const setTheme = async () => {

    if (darkMode === undefined) {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      dispatch(setInitialTheme(prefersDark));
    }

    if (darkMode) {
      document.body.classList.add('dark');
    }

  }


  const setupListeners = () => {

    Network.addListener('networkStatusChange', status => {
      if (!status.connected) { // checks in case upon startup there is not internet connection
        showNetworkAlert();
        return;
      }
    });

    document.addEventListener('ionBackButton', (ev: any) => {
      ev.detail.register(-1, () => {
        if (!ionRouter.canGoBack()) {
          CapacitorApp.exitApp();
        }
      });
    });

    CapacitorApp.addListener('appStateChange', ({ isActive }) => {

      if (isActive) {
        resetInactivityTimer(); // Reset the timer when the app becomes active again
      }

      else {
        dispatch(clearInactivityTimeout()); // Clear the inactivity timeout immediately

      }

    })

  };

  const setupInitialConfig = async () => {

    let status = await Network.getStatus();

    if (!status.connected) { // checks in case upon startup there is not internet connection
      showNetworkAlert();
      return;
    }

    const device_private_key = await storage.get('device_private_key');

    if (device_private_key) {

      const account_key = await storage.get('account_key');
      const device_key = await storage.get('device_key');
      const account_private_key = await storage.get('account_private_key');

      setIsKey(true);

      const walletState: WalletInfoState = {
        has_key: true,
        account_key: account_key,
        account_private_key: account_private_key,
        device_key: device_key,
        device_private_key: device_private_key,
        info: ""
      }

      dispatch(setWalletInfo(walletState))
    }

    if (Capacitor.getPlatform() != 'web') {
      ScreenOrientation.lock({ orientation: "portrait" });
    }

  }


  const idleResetTimer = () => {
    setTimeout(() => {
      resetInactivityTimer();
    }, 50); // Use a small timeout delay to prevent immediate execution
  };

  const checkForUpdates = async () => {

    if (Capacitor.getPlatform() === 'web') return;

    try {
      const appInfo = await CapacitorApp.getInfo();
      const installedNativeVersion = appInfo.version; // e.g., "8.3.3"

      // Fetch policy from backend
      const { data: policy } = await axios.get(`${apiBaseUrl}/app/versions`);

      const platform = Capacitor.getPlatform();
      const p = platform === 'ios' ? policy.ios : policy.android;
      const enforceAt = policy.enforceAt ? Date.parse(policy.enforceAt) : null;

      // set store URL for CTA
      setAppStoreUrl(platform === 'ios' ? (p.storeUrl || appStoreUrlIOS) : (p.storeUrl || appStoreUrlAndroid));

      // Forced update if installed < min and enforcement time reached (or unset)
      if (p?.min && compareSemver(installedNativeVersion, p.min) < 0) {
        if (!enforceAt || Date.now() >= enforceAt) {
          if (p.storeReady) {
            setForceUpdateAlert(true);
          }
          return; // do not show optional prompt if forced
        }
      }

      // Optional update if installed < latest
      if (p?.latest && compareSemver(installedNativeVersion, p.latest) < 0) {
        if (p.storeReady) {
          setShowVersionAlert(true);
        }
      }
    } catch (error) {
      // swallow errors; do not block app startup
      Sentry.captureException(error);
    }

  }

  useEffect(() => {
    // Replace direct calls with idle requests
    document.addEventListener('touchstart', idleResetTimer, false);
    document.addEventListener('click', idleResetTimer, false);
    document.addEventListener('keydown', idleResetTimer, false);

    return () => {
      document.removeEventListener('touchstart', idleResetTimer, false);
      document.removeEventListener('click', idleResetTimer, false);
      document.removeEventListener('keydown', idleResetTimer, false);
      dispatch(clearInactivityTimeout());
    };
  }, [dispatch]);

  useEffect(() => {

    const init = async () => {

      try {
        setupListeners();
        getcustomConfig();

        // Sync Redux language with i18next's detected language
        if (i18n.language && i18n.language !== language) {
          dispatch(setLanguage(i18n.language));
        }

        // Deep link handling is done by AppUrlListener component  
        if (!language) {  // Run only if language isn't already set
          await setDeviceLanguage();
        }

        // Ensure i18n is initialized and translations are loaded
        await i18n.loadNamespaces('translation');

        await setupInitialConfig();

        await setTheme();

        // await liveUpdate();

        await checkForUpdates();

        // Region check
        await checkBlockedRegion();

      } catch (error) {
        Sentry.captureException(error);
      } finally {
        // Mark initialization as complete
        setIsInitialized(true);
      }

    }
    init();


    return () => {
      // Cleanup listeners
      Network.removeAllListeners();
      CapacitorApp.removeAllListeners();
    };

  }, [dispatch, darkMode, language])

  // Hide splash screen after initialization and rendering
  useEffect(() => {
    if (isInitialized) {
      // Wait a tick to ensure DOM is fully rendered
      const timer = setTimeout(async () => {
        if (Capacitor.getPlatform() !== 'web') {
          try {
            await SplashScreen.hide({
              fadeOutDuration: 300
            });
          } catch (error) {
            Sentry.captureException(error);
          }
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isInitialized]);

  useEffect(() => {
    if (language) {
      i18n.changeLanguage(language); // Update i18n language when Redux language changes
    }
  }, [language, i18n]);

  useEffect(() => {
    const initializeSafeArea = async () => {
      if (Capacitor.getPlatform() === 'android') {
        const { insets } = await SafeArea.getSafeAreaInsets();
        // Set CSS variables for safe area insets
        for (const [key, value] of Object.entries(insets)) {
          document.documentElement.style.setProperty(`--safe-area-inset-${key}`, `${value}px`);
        }
      }
    };

    initializeSafeArea();
  }, []);

  const checkBlockedRegion = async () => {
    try {
      const { data } = await axios.get(`${apiBaseUrl}/ip`);
      const countryCode = data?.country_code2;
      if (countryCode && blockedRegions.blocked_countries.includes(countryCode)) {
        setIsBlockedRegion(true);
      }
    } catch (error) {
      // Don't block app on geo-check failure
      Sentry.captureException(error);
    }
  };

  function compareSemver(a: string, b: string): number {
    const i = a.split('.').map(Number);
    const l = b.split('.').map(Number);
    for (let x = 0; x < Math.max(i.length, l.length); x++) {
      const av = i[x] || 0;
      const bv = l[x] || 0;
      if (av < bv) return -1;
      if (av > bv) return 1;
    }
    return 0;
  }


  return (
    <AppProvider>
      <IonApp ref={appRef}>
        <LockScreenModal isOpen={isLockScreenVisible}
          onUnlock={() => dispatch(hideLockScreen())}
        />

        <IonAlert
          isOpen={showAlert}
          onDidDismiss={() => setShowAlert(false)}
          header={t('ALERT.NOT.NETWORK.HEADER') as string}
          message={t('ALERT.NOT.NETWORK.MESSAGE') as string}
          buttons={[{
            text: 'OK',
            role: 'cancel',
            handler: () => {
              setShowAlert(false)
              return;
            }
          },]}
        />
        <IonAlert
          isOpen={versionAlert}
          onDidDismiss={() => setShowVersionAlert(false)}
          header={t('ALERT.NEW.UPATE') as string}
          message={t('ALERT.NEW.UPATE.MESSAGE') as string}
          buttons={[{
            text: `${t('ALERT.NEW.UPATE.BTN')}`,
            role: 'confirm',
            handler: () => {
              setShowVersionAlert(false);
              Browser.open({ url: appStoreUrl! });
              return;

            }
          },]}
        />
        <IonAlert
          isOpen={forceUpdateAlert}
          onDidDismiss={() => setForceUpdateAlert(true)}
          backdropDismiss={false}
          header={t('ALERT.NEW.UPATE') as string}
          message={t('ALERT.NEW.UPATE.MESSAGE') as string}
          buttons={[{
            text: `${t('ALERT.NEW.UPATE.BTN')}`,
            role: 'confirm',
            handler: () => {
              Browser.open({ url: appStoreUrl! });
              setForceUpdateAlert(true); // keep it open on return
              return;
            }
          }]}
        />


        <IonModal isOpen={isBlockedRegion} backdropDismiss={false} canDismiss={false}>
          <UnsupportedRegionModal />
        </IonModal>

        <IonReactRouter>
          <AppUrlListener></AppUrlListener>

          <IonRouterOutlet>

            <Route path="/init" component={InitPage} />
            <Route path="/signup/name" component={NamePage} />
            <Route path="/signup/email" component={EmailPage} />

            <Route path="/signup/phone" component={PhonePage} />

            <Route path="/signup/verify-email" component={EmailVerificationPage} />
            <Route path="/signup/verify-phone" component={PhoneVerificationPage} />
            <Route path="/signup/secure-wallet-info" component={SecureWalletInfoPage} ></Route>
            <Route path="/wallet-generate-intro" component={GenerateWalletIntroPage} />
      
            <Route path="/tabs" component={TabsPage} />
            <Route path='/welcome' component={WelcomePage} />
            <Route exact path="/login" component={LoginWalletPage} />
            <Route exact path="/login/phone" component={LoginPhone} />
            <Route exact path="/login/phone/verify" component={LoginPhoneVerifyPage} />
            <Route exact path="/login/email" component={LoginWithEmail} />
            <Route exact path="/login/email/verify" component={LoginEmailVerify} />
        
            
            {/* <Route path="/recover-account" component={RecoverAccountPage}></Route> */}
            <Route path="/verify/start" component={KycFlowIntroPage} ></Route>
            <Route path="/verify/processing" component={KycProcessing} ></Route>
            <Route path="/verify/rejected" component={KycRejected} ></Route>
            <Route path="/pin/create" component={PinCreatePage} ></Route>
            <Route path="/pin/verify" component={PinVerifyPage} ></Route>
            <Route path="/pin/lock" component={PinLockPage} ></Route>
            <Route exact path="/" component={InitPage}
            />
          </IonRouterOutlet>
        </IonReactRouter>

      </IonApp>
    </AppProvider>
  )

}

const App: React.FC = () => (
  <Suspense fallback={null}>

    <RootComponent />

  </Suspense>
);

export default App;
